/* SignInGate — Microsoft tenant SSO landing.
   Ported from design_handoff_daedalus/design_files/agents.jsx (SignInGate).

   The prototype fakes the authentication with a 1.8s timer. In Phase 1
   this scene will call MSAL.loginRedirect() and resolve identity from
   the preflight-auth response. For Phase 0.5 the flow simulates three
   states (welcome → auth → confirm) against MSW mocks. */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CornerTicks,
  DaedalusWordmark,
  WorkingMaze,
} from '@/components/brand';
import { MOCK_USER } from '@/api/mocks/fixtures';
import { useAuth } from '@/auth/MsalBootstrap';
import { whoami } from '@/api/client';

type Step = 'welcome' | 'auth' | 'confirm';

/* Where to send the user after sign-in when the RequireAuth guard
   bounced them here from a deep link. Stashed in sessionStorage (not
   router state) because loginRedirect is a full-page navigation to
   Microsoft — router state doesn't survive it, sessionStorage does
   (same tab). */
const RETURN_PATH_KEY = 'daedalus.postLoginReturn';

export function SignInGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, mocked, getAccessToken } = useAuth();
  const [step, setStep] = useState<Step>('welcome');
  const guardedFrom = (location.state as { from?: string } | null)?.from;

  // Role-aware landing — call /tools/agents/whoami once we have a user
  // and route to the recommended scene. /home is the fallback when
  // whoami fails OR mock mode is on (no real backend to ask). A deep
  // link remembered by the RequireAuth guard (router state for the
  // same-page case, sessionStorage stash across the MSAL redirect)
  // wins over the role-recommended landing.
  async function landByRole() {
    let returnPath: string | null = guardedFrom ?? null;
    try {
      if (!returnPath) {
        returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
      }
      sessionStorage.removeItem(RETURN_PATH_KEY);
    } catch {
      /* storage unavailable — fall through to role-based landing */
    }
    if (returnPath && returnPath !== '/sign-in') {
      navigate(returnPath, { replace: true });
      return;
    }
    if (mocked) {
      navigate('/home', { replace: true });
      return;
    }
    try {
      const token = await getAccessToken();
      const me = await whoami(token);
      navigate(me?.recommended_landing ?? '/home', { replace: true });
    } catch {
      navigate('/home', { replace: true });
    }
  }

  // If we already have a signed-in session when we land on /sign-in
  // (e.g. after MSAL redirect returns) skip the gate entirely. Only
  // auto-navigate from the welcome step so the mock-mode auth →
  // confirm flow still gets to play.
  useEffect(() => {
    if (user && step === 'welcome') {
      void landByRole();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, step]);

  function beginSignIn() {
    if (!mocked) {
      // Real flow: MSAL PKCE redirect. Control leaves the SPA; on return
      // useAuth().user populates and the useEffect above navigates home.
      // Stash the guard's deep link first so it survives the round-trip
      // to Microsoft. Surface failures (stale interaction state, config
      // errors) instead of letting the rejection vanish — a silently
      // dead Sign-in button is the mobile-Safari failure mode.
      if (guardedFrom && guardedFrom !== '/sign-in') {
        try {
          sessionStorage.setItem(RETURN_PATH_KEY, guardedFrom);
        } catch {
          /* storage unavailable — role-based landing still works */
        }
      }
      login().catch((err) => console.error('[MSAL] loginRedirect failed:', err));
      return;
    }
    // Walkthrough flow: run the visual simulation.
    void login();
    setStep('auth');
    setTimeout(() => setStep('confirm'), 1800);
  }

  function enter() {
    void landByRole();
  }

  return (
    <div className="sg">
      <div className="sg-inner">
        <div className="sg-brand">
          <DaedalusWordmark size={36} />
          <div className="sg-tag">the workshop for construction teams</div>
        </div>

        <div className="sg-card">
          <div className="sg-ticks">
            <CornerTicks />
          </div>

          {step === 'welcome' && (
            <div className="sg-body">
              <div className="mono-lc">preflight · sign in</div>
              <h1>Welcome back.</h1>
              <p>
                Daedalus is the front door to your agentic system. Sign in with your Microsoft
                tenant — we'll resolve your role and scope and pass it to the Foundry agents
                behind the curtain.
              </p>

              <button className="btn-ms" onClick={beginSignIn}>
                <MsLogo />
                <span>Sign in with Microsoft</span>
              </button>

              <div className="sg-foot">
                <span className="mono-lc">tenant</span>
                <span>syncon.build</span>
                <span className="sg-dot">·</span>
                <span className="mono-lc">status</span>
                <span className="sg-ok">● healthy</span>
              </div>
            </div>
          )}

          {step === 'auth' && (
            <div className="sg-body sg-auth">
              <WorkingMaze size={220} />
              <div className="mono-lc">authenticating · entra id</div>
              <div className="sg-auth-steps">
                <div className="sg-auth-step done">• tenant verified</div>
                <div className="sg-auth-step done">• identity resolved</div>
                <div className="sg-auth-step">• loading role + project scope…</div>
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="sg-body">
              <div className="mono-lc">identity resolved</div>
              <h1>You're {MOCK_USER.name.split(' ')[0]}.</h1>
              <div className="sg-ident">
                <div className="sg-ident-row">
                  <span className="mono-lc">role</span>
                  <span>{MOCK_USER.role}</span>
                </div>
                <div className="sg-ident-row">
                  <span className="mono-lc">projects</span>
                  <span>{MOCK_USER.projects.join(', ')}</span>
                </div>
                <div className="sg-ident-row">
                  <span className="mono-lc">scope</span>
                  <span>project-assigned (default)</span>
                </div>
              </div>
              <p className="sg-hint">
                This scope is passed as claims to every Foundry agent call. You'll only see
                project data you're assigned to.
              </p>
              <button className="btn-primary" onClick={enter}>
                enter the workshop →
              </button>
            </div>
          )}
        </div>

        <div className="sg-legal mono-lc">daedalus · v0.1</div>
      </div>

      <style>{`
        .sg {
          flex: 1; min-height: 100vh; min-height: 100dvh;
          display: flex; align-items: center; justify-content: center;
          background:
            linear-gradient(0deg, var(--grid) 1px, transparent 1px),
            linear-gradient(90deg, var(--grid) 1px, transparent 1px),
            var(--bg-1);
          background-size: 48px 48px;
          padding: 48px 24px;
          overflow: auto;
        }
        .sg-inner { width: min(560px, 100%); display: flex; flex-direction: column; gap: 24px; }
        .sg-brand { text-align: center; color: var(--ink); }
        .sg-tag {
          color: var(--ink-3);
          font-family: 'IBM Plex Serif', Georgia, serif;
          font-style: italic;
          margin-top: 8px;
        }
        .sg-card {
          position: relative;
          background: var(--bg-2); border: 1px solid var(--rule-soft);
          padding: 40px 40px 36px;
        }
        .sg-ticks { position: absolute; inset: 6px; pointer-events: none; }
        .sg-body { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 16px; }
        .sg-body h1 {
          font-family: 'IBM Plex Serif', Georgia, serif;
          font-weight: 400;
          font-size: 32px;
          margin: 4px 0 8px;
          letter-spacing: -0.01em;
        }
        .sg-body p { color: var(--ink-2); margin: 0; line-height: 1.55; }
        .btn-ms {
          display: flex; gap: 10px;
          align-items: center; justify-content: center;
          padding: 12px 16px; background: var(--ink); color: var(--bg-1);
          border: 0; font: inherit; font-size: 15px; cursor: pointer;
          margin-top: 12px;
        }
        .btn-ms:hover { background: var(--accent); }
        .btn-primary {
          padding: 12px 16px; background: var(--accent); color: var(--accent-ink);
          border: 0; font: inherit; font-size: 15px; cursor: pointer;
          margin-top: 12px;
        }
        .btn-primary:hover { background: var(--accent-2); }
        .sg-foot {
          display: flex; gap: 8px; align-items: center;
          margin-top: 16px; padding-top: 16px;
          border-top: 1px dashed var(--rule-hair);
          font-size: 13px; color: var(--ink-2);
        }
        .sg-foot .mono-lc { color: var(--ink-3); }
        .sg-dot { color: var(--ink-3); }
        .sg-ok { color: #5d7a58; }
        .sg-auth { align-items: center; text-align: center; }
        .sg-auth-steps {
          display: flex; flex-direction: column; gap: 6px;
          font-size: 13px; color: var(--ink-2); margin-top: 8px;
        }
        .sg-auth-step { font-family: 'JetBrains Mono', monospace; }
        .sg-auth-step.done { color: var(--ink-3); }
        .sg-ident {
          display: flex; flex-direction: column; gap: 0;
          background: var(--bg-1); border: 1px solid var(--rule-hair);
          margin-top: 8px;
        }
        .sg-ident-row {
          display: grid; grid-template-columns: 120px 1fr; gap: 14px;
          padding: 10px 14px; border-bottom: 1px solid var(--rule-hair);
          font-size: 14px; color: var(--ink);
        }
        .sg-ident-row:last-child { border-bottom: 0; }
        .sg-ident-row .mono-lc { color: var(--ink-3); }
        .sg-hint { font-size: 13px; color: var(--ink-3); margin-top: 4px; }
        .sg-legal { text-align: center; color: var(--ink-3); }
        @media (max-width: 480px) {
          .sg { padding: 20px 12px; }
          .sg-card { padding: 24px 20px 22px; }
          .sg-foot { flex-wrap: wrap; row-gap: 4px; }
          .sg-body h1 { font-size: 26px; }
        }
      `}</style>
    </div>
  );
}

function MsLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6.5" height="6.5" fill="#F25022" />
      <rect x="8.5" y="1" width="6.5" height="6.5" fill="#7FBA00" />
      <rect x="1" y="8.5" width="6.5" height="6.5" fill="#00A4EF" />
      <rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#FFB900" />
    </svg>
  );
}
